import { useEffect, useRef, useState } from "react";
import { usePlayer } from "@/context/PlayerContext";
import { useI18n } from "@/context/I18nContext";
import { coverFor, formatTime } from "@/lib/api";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Maximize2, Minimize2, Lock, Shuffle, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function MiniPlayer() {
  const {
    current, playing, progress, duration, volume, muted, fullscreen,
    previewMode, previewLimit, shuffle,
    setVolume, setMuted, setFullscreen, setShuffle, setShuffleSource, toggle, next, prev, seek,
  } = usePlayer();
  const { t } = useI18n();
  const [hiddenOnMobile, setHiddenOnMobile] = useState(false);
  const touchStart = useRef(null);

  useEffect(() => {
    setHiddenOnMobile(false);
  }, [current?.id]);

  if (!current) return null;
  const effDur = previewMode ? Math.min(previewLimit, duration || previewLimit) : duration;
  const touchHandlers = {
    onTouchStart: (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      touchStart.current = { x: touch.clientX, y: touch.clientY };
    },
    onTouchEnd: (e) => {
      const start = touchStart.current;
      const touch = e.changedTouches?.[0];
      touchStart.current = null;
      if (!start || !touch) return;
      const deltaX = touch.clientX - start.x;
      const deltaY = Math.abs(touch.clientY - start.y);
      if (deltaX < -48 && deltaY < 70) {
        setHiddenOnMobile(true);
      }
    },
  };

  return (
    <>
      {hiddenOnMobile && (
        <button
          type="button"
          onClick={() => setHiddenOnMobile(false)}
          data-testid="mini-player-show-btn"
          className="fixed bottom-[86px] left-2 z-40 flex items-center gap-1.5 rounded-r-full border border-l-0 border-[#D4A23A]/30 bg-[#121212]/95 py-2 pl-2 pr-3 text-xs font-black text-[#D4A23A] shadow-2xl shadow-black/40 backdrop-blur sm:hidden"
          aria-label="Afiseaza playerul"
        >
          <ChevronRight size={16} /> Player
        </button>
      )}

      <div
        data-testid="mini-player"
        {...touchHandlers}
        className={`glass fixed bottom-[72px] left-0 z-40 w-full px-3 py-2 transition-transform duration-300 sm:bottom-0 sm:z-50 sm:px-6 sm:py-3 ${
          hiddenOnMobile ? "-translate-x-[calc(100%+24px)] sm:translate-x-0" : "translate-x-0"
        }`}
      >
        <div className="mx-auto mb-1 h-1 w-12 rounded-full bg-white/20 sm:hidden" aria-hidden="true" />
        <div className="mx-auto flex max-w-7xl items-center gap-3 sm:gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <img src={coverFor(current)} alt="" className="h-10 w-10 flex-shrink-0 rounded-md object-cover sm:h-12 sm:w-12" />
            <div className="min-w-0">
              <div data-testid="player-track-title" className="truncate text-sm font-semibold">{current.title}</div>
              <div className="truncate text-xs text-zinc-400">{current.artist}</div>
            </div>
          </div>

          <div className="flex shrink-0 flex-col items-center gap-1 sm:flex-1">
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShuffle((v) => !v);
                  setShuffleSource("");
                }}
                data-testid="player-shuffle-btn"
                className={`rounded-full px-2.5 py-1.5 text-xs font-black transition ${
                  shuffle ? "bg-[#D4A23A] text-black shadow-[0_0_20px_rgba(212,162,58,0.25)]" : "text-zinc-400 hover:bg-white/5 hover:text-white"
                }`}
                title={shuffle ? "Shuffle activ" : "Activeaza shuffle"}
              >
                <span className="flex items-center gap-1.5">
                  <Shuffle size={15} /> <span className="hidden md:inline">{shuffle ? "Shuffle activ" : "Shuffle"}</span>
                </span>
              </button>
              <button onClick={prev} data-testid="player-prev-btn" className="hidden text-zinc-400 transition hover:text-white sm:inline-flex">
                <SkipBack size={18} fill="currentColor" />
              </button>
              <button
                onClick={toggle}
                data-testid="player-toggle-btn"
                className="grid h-10 w-10 place-items-center rounded-full bg-[#D4A23A] text-black transition hover:scale-105"
              >
                {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
              </button>
              <button onClick={next} data-testid="player-next-btn" className="text-zinc-400 transition hover:text-white">
                <SkipForward size={18} fill="currentColor" />
              </button>
            </div>
            <div className="hidden w-full max-w-md items-center gap-2 sm:flex">
              <span className="font-mono text-[10px] text-zinc-500">{formatTime(progress)}</span>
              <input
                type="range" className="sm-slider flex-1"
                min={0} max={effDur || 0} step={0.1}
                value={progress}
                onInput={(e) => seek(parseFloat(e.target.value))}
                onChange={(e) => seek(parseFloat(e.target.value))}
                data-testid="player-seek"
              />
              <span className="font-mono text-[10px] text-zinc-500">{formatTime(effDur)}</span>
            </div>
            {previewMode && (
              <Link
                to="/dashboard"
                data-testid="player-preview-banner"
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#D4A23A] hover:underline"
              >
                <Lock size={11} /> {t("catalog.previewOnly")}
              </Link>
            )}
          </div>

          <div className="hidden flex-1 items-center justify-end gap-2 sm:flex">
            <button onClick={() => setMuted(!muted)} data-testid="player-mute-btn" className="text-zinc-400 hover:text-white">
              {muted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <input
              type="range" className="sm-slider w-24" min={0} max={1} step={0.01}
              value={muted ? 0 : volume}
              onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
              data-testid="player-volume"
            />
            <button
              onClick={() => setFullscreen(!fullscreen)}
              data-testid="player-fullscreen-btn"
              className="ml-2 rounded-full p-1.5 text-zinc-400 hover:bg-white/5 hover:text-white"
            >
              {fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          </div>
        </div>
      </div>

      {fullscreen && <FullPlayer />}
    </>
  );
}

function FullPlayer() {
  const { current, playing, progress, duration, previewMode, previewLimit, shuffle, setShuffle, setShuffleSource, toggle, next, prev, seek, setFullscreen } = usePlayer();
  const effDur = previewMode ? Math.min(previewLimit, duration || previewLimit) : duration;
  return (
    <div
      data-testid="full-player"
      className="fixed inset-0 z-[60] bg-[#0A0A0A]/95 backdrop-blur-2xl"
      onClick={() => setFullscreen(false)}
    >
      <div className="mx-auto flex h-full max-w-3xl flex-col items-center justify-center gap-8 px-6" onClick={(e) => e.stopPropagation()}>
        <img
          src={coverFor(current)}
          alt={current.title}
          className="aspect-square w-full max-w-md rounded-2xl object-cover shadow-[0_50px_120px_-30px_rgba(212,255,0,0.3)]"
        />
        <div className="text-center">
          <h2 className="font-display text-4xl font-black tracking-tight">{current.title}</h2>
          <p className="mt-2 text-lg text-zinc-400">{current.artist}</p>
          <p className="mt-1 text-xs uppercase tracking-[0.3em] text-[#D4A23A]">{current.genre} · {current.mood}</p>
        </div>
        <div className="w-full max-w-md">
          <input
            type="range" className="sm-slider w-full" min={0} max={effDur || 0} step={0.1} value={progress}
            onInput={(e) => seek(parseFloat(e.target.value))}
            onChange={(e) => seek(parseFloat(e.target.value))}
          />
          <div className="mt-1 flex justify-between font-mono text-xs text-zinc-500">
            <span>{formatTime(progress)}</span>
            <span>{formatTime(effDur)}</span>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <button
            onClick={() => {
              setShuffle((v) => !v);
              setShuffleSource("");
            }}
            className={`rounded-full px-4 py-2 font-black transition ${
              shuffle ? "bg-[#D4A23A] text-black shadow-[0_0_24px_rgba(212,162,58,0.25)]" : "text-zinc-400 hover:bg-white/5 hover:text-white"
            }`}
            title={shuffle ? "Shuffle activ" : "Activeaza shuffle"}
          >
            <span className="flex items-center gap-2"><Shuffle size={22} /> {shuffle ? "Shuffle activ" : "Shuffle"}</span>
          </button>
          <button onClick={prev} className="text-zinc-400 hover:text-white"><SkipBack size={28} fill="currentColor" /></button>
          <button onClick={toggle} className="grid h-16 w-16 place-items-center rounded-full bg-[#D4A23A] text-black">
            {playing ? <Pause size={28} fill="currentColor" /> : <Play size={28} fill="currentColor" />}
          </button>
          <button onClick={next} className="text-zinc-400 hover:text-white"><SkipForward size={28} fill="currentColor" /></button>
        </div>
        <button
          onClick={() => setFullscreen(false)}
          data-testid="full-player-close-btn"
          className="rounded-full border border-white/10 px-5 py-2 text-sm text-zinc-300 hover:bg-white/5"
        >
          Close
        </button>
      </div>
    </div>
  );
}
